import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Row, Col } from 'react-bootstrap';
import { BiServer } from 'react-icons/bi';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import AppContext from 'Utils/AppContext';
import { listTargetGroups, createTargetGroup, updateTargetGroup, deleteTargetGroup, errorText } from '../tdmApi';
import { engineBadge, engineLabel, TDM_TARGET_RULES } from '../tdmUtils';
import SourcePicker from 'DataSources/SourcePicker';
import { useConfirm } from 'Utils/ConfirmDialog';
import Breadcrumbs from 'Utils/Breadcrumbs';

const blank = () => ({ name: '', description: '', members: [] });

// Members come from the API with their own name/engine; the connection list only adds host and
// database, so a group whose member connection has since been removed still renders.
const fromResponse = (g) => ({
  name: g.name || '',
  description: g.description || '',
  members: (g.members || []).map((m) => ({ dataSourceId: m.dataSourceId, dataSourceName: m.dataSourceName, engine: m.engine })),
});

// Target groups: a named set of connections a subset can be provisioned into. Same shape as the
// staging manager — list on the left, editor for the selected one on the right. Writes are
// admin-only on the platform (403 otherwise), so the form is read-only for everyone else.
const TargetGroupsScreen = () => {
  const { confirm, confirmDialog } = useConfirm();
  const { connections: allConnections, isAdmin } = useContext(AppContext);
  const connections = useMemo(() => (Array.isArray(allConnections) ? allConnections : []), [allConnections]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);   // null = new
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    try {
      const list = await listTargetGroups();
      const arr = Array.isArray(list) ? list : [];
      setItems(arr);
      setError(null);
      return arr;
    } catch (e) {
      setError(errorText(e, 'Could not load target groups'));
      return [];
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selected = items.find((g) => String(g.id) === String(selectedId)) || null;

  // Selection fills the form from the list item here, not in an effect on selectedId: after a
  // save the selection moves to the saved id and the success notice has to survive that.
  const select = (g) => {
    setNotice(null);
    setSelectedId(g ? g.id : null);
    setForm(g ? fromResponse(g) : blank());
  };

  const byId = useMemo(() => {
    const m = new Map();
    connections.forEach((c) => m.set(String(c.id), c));
    return m;
  }, [connections]);
  const memberIds = form.members.map((m) => m.dataSourceId);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addMember = (idString) => {
    const c = byId.get(String(idString));
    if (!c) return;
    setForm((f) => (f.members.some((m) => String(m.dataSourceId) === String(c.id)) ? f : {
      ...f,
      members: [...f.members, { dataSourceId: c.id, dataSourceName: c.name, engine: c.type || c.engine }],
    }));
  };
  const removeMember = (dataSourceId) => setForm((f) => ({
    ...f, members: f.members.filter((m) => String(m.dataSourceId) !== String(dataSourceId)),
  }));

  const save = async (e) => {
    if (e) e.preventDefault();
    if (!isAdmin || !form.name.trim() || form.members.length === 0) return;
    setSaving(true);
    setNotice(null);
    try {
      const body = {
        name: form.name,
        description: form.description,
        members: form.members.map((m) => ({ dataSourceId: m.dataSourceId })),
      };
      const saved = selected ? await updateTargetGroup(selected.id, body) : await createTargetGroup(body);
      const list = await load();
      const fresh = list.find((g) => String(g.id) === String(saved.id));
      setSelectedId(saved.id);
      if (fresh) setForm(fromResponse(fresh));
      setNotice({ tone: 'success', text: `Saved ${saved.name || form.name}.` });
    } catch (err) {
      setNotice({ tone: 'danger', text: errorText(err, 'Save failed') });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selected) return;
    if (!await confirm({ title: `Delete target group "${selected.name}"?`, body: 'The connections in it are not touched. Only the group goes away.', confirmLabel: 'Delete', variant: 'danger' })) return;
    setNotice(null);
    try {
      await deleteTargetGroup(selected.id);
      select(null);
      await load();
    } catch (err) {
      setNotice({ tone: 'danger', text: errorText(err, 'Delete failed') });
    }
  };

  const canSave = !(saving || !isAdmin || !form.name.trim() || form.members.length === 0);

  return (
    <FetchContainer fetchingData={loading} absolute={true} error={error}>
      <div className='tdm-page tdm-page--narrow'>
      {confirmDialog}
        <Breadcrumbs items={[{ label: 'Test Data Landscape', to: '/tdm' }, { label: 'Target groups' }]} />
        <PageHeader
          icon={BiServer}
          eyebrow='Test Data Management'
          header='Target groups'
          tooltip='Reusable sets of target connections'
          meta={<span>A named set of connections a subset can be deployed into. On the Deploy screen a group is spread over the sources it supports, one target per engine.</span>}
          primaryAction={<Button size='sm' data-testid='tdm-tg-new' onClick={() => select(null)}>+ New</Button>}
        />
        <div className='tdm-staging-layout'>
          <div>
            <div className='tdm-column__title'><span>Target groups · {items.length}</span></div>
            {items.length === 0 && <div className='tdm-empty'>No target groups yet — create one to deploy subsets into the same connections every time.</div>}
            {items.map((g) => {
              const on = String(g.id) === String(selectedId);
              const n = (g.members || []).length;
              return (
                <button type='button' key={g.id} className={`tdm-list-item${on ? ' tdm-list-item--on' : ''}`}
                  onClick={() => select(g)} data-testid={`tdm-tg-${g.id}`}>
                  <div className='d-flex justify-content-between align-items-center'>
                    <span className='tdm-list-item__name'>{g.name}</span>
                    <span className='tdm-badges'>
                      {(g.members || []).map((m) => <span key={m.dataSourceId} className='tdm-badge' title={m.dataSourceName}>{engineBadge(m.engine)}</span>)}
                    </span>
                  </div>
                  {g.description && <div className='tdm-list-item__sub'>{g.description}</div>}
                  <div className='tdm-list-item__sub'>{n} target{n === 1 ? '' : 's'}</div>
                </button>
              );
            })}
          </div>

          <div>
            <Form onSubmit={save} className='tdm-section'>
              <div className='tdm-section__title'>{selected ? selected.name : 'New target group'}</div>
              <div className='tdm-section__sub'>Pick the connections a subset should land in. Deploying writes rows, so only writable connections are offered.</div>
              <Row className='text-left mb-2'>
                <Col xs={6}><Form.Group><Form.Label>Name <span className='text-danger'>*</span></Form.Label>
                  <Form.Control size='sm' value={form.name} onChange={(e) => update('name', e.target.value)} required readOnly={!isAdmin} data-testid='tdm-tg-name' /></Form.Group></Col>
                <Col xs={6}><Form.Group><Form.Label>Description</Form.Label>
                  <Form.Control size='sm' value={form.description} onChange={(e) => update('description', e.target.value)} readOnly={!isAdmin} data-testid='tdm-tg-description' /></Form.Group></Col>
              </Row>

              <Form.Label className='d-block mb-1'>Targets · {form.members.length}</Form.Label>
              {form.members.length === 0 && <div className='tdm-empty' data-testid='tdm-tg-no-members'>No targets yet — add at least one below.</div>}
              {form.members.length > 0 && (
                <div className='tdm-table tdm-table--target-members' data-testid='tdm-tg-members'>
                  <div className='tdm-table__head'><span /><span>Connection</span><span>Engine</span><span>Host</span><span>Database</span><span /></div>
                  {form.members.map((m) => {
                    const c = byId.get(String(m.dataSourceId));
                    const engine = m.engine || (c && (c.type || c.engine));
                    return (
                      <div className='tdm-table__row' key={m.dataSourceId} data-testid={`tdm-tg-member-${m.dataSourceId}`}>
                        <span className='tdm-badge'>{engineBadge(engine)}</span>
                        <span className='dg-name'>{m.dataSourceName || (c && c.name) || `#${m.dataSourceId}`}</span>
                        <span className='tdm-muted'>{engineLabel(c || { type: engine })}</span>
                        <span className='tdm-muted tdm-clip' title={(c && c.host) || ''}>{(c && (c.host || (c.loginUrl ? String(c.loginUrl).replace(/^https?:\/\//, '') : ''))) || '—'}</span>
                        <span className='tdm-muted tdm-clip' title={(c && c.database) || ''}>{(c && (c.database || c.username)) || '—'}</span>
                        <span>
                          <Button size='sm' variant='outline-danger' className='tdm-btn-xs' type='button' disabled={!isAdmin}
                            onClick={() => removeMember(m.dataSourceId)} data-testid={`tdm-tg-remove-${m.dataSourceId}`}>Remove</Button>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {isAdmin && (
                <div className='mt-3'>
                  {/* Read-only connections are left out with their reason: deploying writes rows. */}
                  <SourcePicker
                    sources={connections}
                    restrict={TDM_TARGET_RULES}
                    exclude={memberIds}
                    title='Add a target'
                    verb='Add target'
                    emptyMessage='Every writable connection is already in this group.'
                    maxHeight={300}
                    onConfirm={(rows) => addMember(rows[0].id)}
                    testId='tdm-tg-target'
                  />
                </div>
              )}

              {notice && <Alert variant={notice.tone} className='py-1 px-2 mb-2 mt-3' data-testid='tdm-tg-notice'>{notice.text}</Alert>}
              <div className='tdm-actions mt-3'>
                <Button size='sm' type='submit' disabled={!canSave} data-testid='tdm-tg-save'>{selected ? 'Save target group' : 'Create target group'}</Button>
                {selected && isAdmin && <Button size='sm' variant='outline-danger' type='button' onClick={remove} data-testid='tdm-tg-delete'>Delete</Button>}
                {!isAdmin && <span className='tdm-note' data-testid='tdm-tg-readonly'>Only administrators can change target groups.</span>}
              </div>
            </Form>
          </div>
        </div>
      </div>
    </FetchContainer>
  );
};

export default TargetGroupsScreen;
